import type { WidgetFC, WidgetProps } from '../../common/Widget/types';
import React from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import useEndUserRoutes from '@aurora/shared-client/routes/useEndUserRoutes';
import localStyles from './UserListForSearchWidget.module.pcss';
import type { ListVariantTypeAndProps } from '@aurora/shared-client/components/common/List';
import { ListVariant } from '@aurora/shared-client/components/common/List/enums';
import type { NodeSearchViewFragment, UserSearchViewFragment } from '../../../types/graphql-types';
import { EndUserQueryParams } from '@aurora/shared-types/pages/enums';
import UserListForSearchTerm from '../../users/UserListForSearchTerm/UserListForSearchTerm';
import { PanelType } from '@aurora/shared-client/components/common/Panel/enums';
import type { PagerVariantTypeAndProps } from '@aurora/shared-client/components/common/Pager/types';
import type { EndUserPages } from '@aurora/shared-types/pages/enums';
import { PagerPosition, PagerVariant } from '@aurora/shared-client/components/common/Pager/enums';
import type { ItemViewVariantProps } from '../../entities/types';
import type { ItemType, UserViewVariant } from '../../../types/enums';

interface Props extends WidgetProps {
  /**
   * Class name(s) to apply to the wrapping element.
   */
  className?: string;
}

/**
 * Renders messages in search result page
 *
 * @author Kiran Kumari
 */
const UserListForSearchWidget: WidgetFC<Props> = ({ className }) => {
  const cx = useClassNameMapper(localStyles);
  const { router } = useEndUserRoutes();
  const queryTerm = router.getUnwrappedQueryParam(EndUserQueryParams.SEARCH_KEY);

  const secondaryListPagerVariant: PagerVariantTypeAndProps<EndUserPages, EndUserQueryParams> = {
    type: PagerVariant.LOAD_MORE,
    props: {
      position: PagerPosition.START
    }
  };

  /**
   * Function to get list variant types and props for secondary search list
   *
   * @returns Secondary ListVariantTypeAndProps
   */
  function getSecondaryListVariant<
    T extends NodeSearchViewFragment | UserSearchViewFragment
  >(): ListVariantTypeAndProps<T> {
    return {
      type: ListVariant.UNSTYLED,
      props: {
        listItemClassName: (): string => cx('lia-search-item')
      }
    };
  }

  const userViewVariantPropsOverrides: ItemViewVariantProps<ItemType.USER, UserViewVariant.INLINE> =
    {
      className: cx('lia-content-box lia-user-content-box'),
      useTopicsCount: true,
      useSolutionCount: true,
      useTopicAndSolutionCountInline: true,
      useHoverCard: false,
      useEntireItemAsLink: true
    };

  return (
    <UserListForSearchTerm
      headerClassname={cx('lia-userlist-header')}
      queryTerm={queryTerm}
      listVariant={getSecondaryListVariant<UserSearchViewFragment>()}
      userViewVariantPropsOverrides={userViewVariantPropsOverrides}
      pagerVariant={secondaryListPagerVariant}
      panel={PanelType.STANDARD}
      useEmpty
      paneledListClassname={className}
    />
  );
};

export default UserListForSearchWidget;
